/**
 * Icon — curated inline SVG family for CevOp.
 *
 * Round caps, 1.75px default stroke, currentColor. No emoji. Apps must use this
 * set rather than a second icon library.
 */
export type IconName =
  | 'home' | 'wallet' | 'receipt' | 'box' | 'chart' | 'brain' | 'settings'
  | 'plus' | 'minus' | 'search' | 'close' | 'check' | 'alert' | 'lock'
  | 'chevron-down' | 'chevron-right' | 'chevron-left' | 'menu' | 'more-horizontal' | 'apps' | 'logout' | 'edit' | 'trash'
  | 'arrow-down' | 'arrow-up' | 'sun' | 'moon' | 'monitor' | 'camera' | 'send' | 'sparkles'
  | 'store' | 'users' | 'retry' | 'eye' | 'eye-off' | 'download' | 'filter' | 'clock' | 'bell'
  | 'briefcase' | 'building' | 'phone' | 'video' | 'mic' | 'mail';

const P: Record<IconName, JSX.Element> = {
  home: <><path d="M4 10.5 12 4l8 6.5" /><path d="M6 10v9h12v-9" /></>,
  wallet: (
    <>
      <path d="M4 8.5h14.5a1.5 1.5 0 0 1 1.5 1.5v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8.5Z" />
      <path d="M4 8.5 6.2 5.2A1.5 1.5 0 0 1 7.5 4.5h10" />
      <path d="M16.5 13.25h2.75v2.5H16.5a1.25 1.25 0 0 1 0-2.5Z" />
    </>
  ),
  receipt: (
    <>
      <path d="M7 4.5h10a1 1 0 0 1 1 1V19l-2-1.1-2 1.1-2-1.1-2 1.1-2-1.1-2 1.1V5.5a1 1 0 0 1 1-1Z" />
      <path d="M9 9h6M9 12.5h6M9 16h3.5" />
    </>
  ),
  box: (
    <>
      <path d="M12 3.5 4.5 7.5v9L12 20.5l7.5-4v-9Z" />
      <path d="m4.5 7.5 7.5 4 7.5-4M12 11.5V20.5" />
    </>
  ),
  chart: <><path d="M5 19V11M10 19V6M15 19v-5M20 19H4" /></>,
  brain: (
    <>
      <path d="M9.2 5a2.8 2.8 0 0 0-2.7 2.8 2.8 2.8 0 0 0-1 5.4V15a2.8 2.8 0 0 0 3.6 2.6A2.4 2.4 0 0 0 12 19.5V5.8A1.9 1.9 0 0 0 9.2 5Z" />
      <path d="M14.8 5a2.8 2.8 0 0 1 2.7 2.8 2.8 2.8 0 0 1 1 5.4V15a2.8 2.8 0 0 1-3.6 2.6A2.4 2.4 0 0 1 12 19.5" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 3.5v2.2M12 18.3V20.5M4.8 4.8l1.6 1.6M17.6 17.6l1.6 1.6M3.5 12h2.2M18.3 12h2.2M4.8 19.2l1.6-1.6M17.6 6.4l1.6-1.6" />
    </>
  ),
  plus: <path d="M12 5.5v13M5.5 12h13" />,
  minus: <path d="M5.5 12h13" />,
  search: <><circle cx="11" cy="11" r="6.5" /><path d="m20 20-3.6-3.6" /></>,
  close: <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />,
  check: <path d="m20 7-8.8 10L5 11.5" />,
  alert: (
    <>
      <path d="M10.5 4.4 3.2 17.2A1.8 1.8 0 0 0 4.8 20h14.4a1.8 1.8 0 0 0 1.6-2.8L13.5 4.4a1.8 1.8 0 0 0-3 0Z" />
      <path d="M12 9.5v4.2M12 16.8h.01" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10.5" width="14" height="9.5" rx="2" />
      <path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" />
    </>
  ),
  eye: (
    <>
      <path d="M2.8 12s3.2-6.5 9.2-6.5S21.2 12 21.2 12s-3.2 6.5-9.2 6.5S2.8 12 2.8 12Z" />
      <circle cx="12" cy="12" r="2.8" />
    </>
  ),
  'eye-off': (
    <>
      <path d="M9.7 5A8.4 8.4 0 0 1 12 4.6c6 0 9.2 7.4 9.2 7.4a12 12 0 0 1-2 2.6M7 7.2A12 12 0 0 0 2.8 12S6 18.5 12 18.5a8.2 8.2 0 0 0 4-1" />
      <path d="M4 4l16 16" />
    </>
  ),
  'chevron-down': <path d="m6.5 9.5 5.5 5.5 5.5-5.5" />,
  'chevron-right': <path d="m9.5 6.5 5.5 5.5-5.5 5.5" />,
  'chevron-left': <path d="m14.5 17.5-5.5-5.5 5.5-5.5" />,
  menu: <path d="M4.5 7h15M4.5 12h15M4.5 17h15" />,
  'more-horizontal': (
    <>
      <circle cx="5.5" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="18.5" cy="12" r="1.5" fill="currentColor" stroke="none" />
    </>
  ),
  apps: (
    <>
      <rect x="3.5" y="3.5" width="4.6" height="4.6" rx="1.1" />
      <rect x="9.7" y="3.5" width="4.6" height="4.6" rx="1.1" />
      <rect x="15.9" y="3.5" width="4.6" height="4.6" rx="1.1" />
      <rect x="3.5" y="9.7" width="4.6" height="4.6" rx="1.1" />
      <rect x="9.7" y="9.7" width="4.6" height="4.6" rx="1.1" />
      <rect x="15.9" y="9.7" width="4.6" height="4.6" rx="1.1" />
      <rect x="3.5" y="15.9" width="4.6" height="4.6" rx="1.1" />
      <rect x="9.7" y="15.9" width="4.6" height="4.6" rx="1.1" />
      <rect x="15.9" y="15.9" width="4.6" height="4.6" rx="1.1" />
    </>
  ),
  logout: <><path d="M9.5 20.5H5.5a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2h4" /><path d="m16 16.5 4.5-4.5L16 7.5M20.5 12H9.5" /></>,
  edit: <><path d="M12.5 19.5h8" /><path d="M16.2 4.8a2 2 0 0 1 2.8 2.8L8 18.6l-3.6.9.9-3.6Z" /></>,
  trash: (
    <>
      <path d="M4.5 7h15" />
      <path d="M8.5 7V5.2A1.7 1.7 0 0 1 10.2 3.5h3.6A1.7 1.7 0 0 1 15.5 5.2V7" />
      <path d="M7 7l.8 13a1.8 1.8 0 0 0 1.8 1.6h4.8a1.8 1.8 0 0 0 1.8-1.6L17 7" />
    </>
  ),
  'arrow-down': <path d="M12 5v14M6 13l6 6 6-6" />,
  download: <><path d="M12 4v11.5M7.5 11l4.5 4.5L16.5 11" /><path d="M5 20h14" /></>,
  filter: <path d="M4 5.5h16l-6.2 7.2v5.3L10.2 16.5v-3.8Z" />,
  clock: <><circle cx="12" cy="12" r="8.2" /><path d="M12 7.5V12l3 2" /></>,
  bell: <><path d="M6.5 9a5.5 5.5 0 1 1 11 0c0 6.2 2.5 8 2.5 8H4s2.5-1.8 2.5-8" /><path d="M10 20a2 2 0 0 0 4 0" /></>,
  'arrow-up': <path d="M12 19V5M6 11l6-6 6 6" />,
  sun: (
    <>
      <circle cx="12" cy="12" r="3.6" />
      <path d="M12 3.2v1.8M12 19v1.8M4.9 4.9l1.3 1.3M17.8 17.8l1.3 1.3M3.2 12h1.8M19 12h1.8M4.9 19.1l1.3-1.3M17.8 6.2l1.3-1.3" />
    </>
  ),
  moon: <path d="M20 13.2A8.2 8.2 0 1 1 10.8 4a6.4 6.4 0 0 0 9.2 9.2Z" />,
  monitor: <><rect x="3" y="4" width="18" height="12.5" rx="2" /><path d="M8.5 20.5h7M12 16.5v4" /></>,
  camera: (
    <>
      <path d="M4.5 8.2A1.8 1.8 0 0 1 6.3 6.4h1.7l1.4-1.9h5.2l1.4 1.9h1.7A1.8 1.8 0 0 1 19.5 8.2v8.6a1.8 1.8 0 0 1-1.8 1.8H6.3a1.8 1.8 0 0 1-1.8-1.8Z" />
      <circle cx="12" cy="12.6" r="3.1" />
    </>
  ),
  send: <path d="M20.5 3.5 10.8 13.2M20.5 3.5 14.2 20.5l-3.4-7.8-7.8-3.4Z" />,
  sparkles: <path d="M12 3.5l1.5 4.2L18 9l-4.5 1.3L12 14.5l-1.5-4.2L6 9l4.5-1.3Z" />,
  store: (
    <>
      <path d="M4 9.2 5.4 4.5h13.2L20 9.2" />
      <path d="M5 9.2a2 2 0 0 0 3.5 0 2 2 0 0 0 3.5 0 2 2 0 0 0 3.5 0 2 2 0 0 0 3.5 0" />
      <path d="M6 10.8v8.7h12v-8.7" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8.2" r="3" />
      <path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" />
      <path d="M16.2 6.2a3 3 0 0 1 0 5.6M20.5 19.5a5.5 5.5 0 0 0-3.6-5.1" />
    </>
  ),
  retry: <><path d="M4 12a8 8 0 1 0 2.6-6L4 8.2" /><path d="M4 4v4.2h4.2" /></>,
  briefcase: (
    <>
      <rect x="3.5" y="7.5" width="17" height="12" rx="2" />
      <path d="M8.5 7.5V5.8A1.8 1.8 0 0 1 10.3 4h3.4A1.8 1.8 0 0 1 15.5 5.8v1.7" />
      <path d="M3.5 12.2h17" />
    </>
  ),
  building: (
    <>
      <path d="M4.5 20.5V5.5A1 1 0 0 1 5.5 4.5h5v16" />
      <path d="M14.5 20.5V9.5A1 1 0 0 1 15.5 8.5h4v12" />
      <path d="M8 9h1.2M8 12.5h1.2M8 16h1.2M17.8 13h1.2M17.8 16.5h1.2" />
    </>
  ),
  phone: <path d="M7 4.2c.5-.5 1.4-.5 1.9 0l1.4 1.4c.5.5.5 1.4 0 1.9L9.2 8.7c.8 1.5 1.9 2.6 3.4 3.4l1.2-1.2c.5-.5 1.4-.5 1.9 0l1.4 1.4c.5.5.5 1.4 0 1.9l-.8.8c-1.1 1.1-2.8 1.4-4.3.5C9.2 14.5 6.6 12 5 8.6c-.9-1.5-.6-3.2.5-4.3z" />,
  video: <><rect x="2.8" y="6.5" width="13" height="11" rx="2" /><path d="m15.8 10 5.4-2.6v9.2L15.8 14" /></>,
  mic: <><rect x="9.2" y="3" width="5.6" height="11" rx="2.8" /><path d="M6 11.5a6 6 0 0 0 12 0M12 17.5v3M8.5 20.5h7" /></>,
  mail: <><rect x="3.5" y="5.5" width="17" height="13" rx="2" /><path d="m4 8 8 5.4L20 8" /></>,
};

export function Icon({
  name,
  size = 22,
  strokeWidth = 1.75,
  className,
  label,
}: {
  name: IconName;
  size?: number;
  strokeWidth?: number;
  className?: string;
  label?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      style={{ flexShrink: 0, display: 'block' }}
    >
      {P[name]}
    </svg>
  );
}
